/**
 * Перечисления домена.
 *
 * SQLite через Prisma не поддерживает нативные enum-типы, поэтому в БД все эти
 * значения хранятся строками, а единственным источником правды являются
 * константы ниже. Их же используют zod-схемы и фронтенд.
 */

export const USER_ROLES = ['USER', 'SUPERADMIN'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const WORKSPACE_ROLES = ['OWNER', 'ADMIN', 'MANAGER', 'VIEWER'] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

export const LOCALES = ['ru', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const THEMES = ['light', 'dark', 'system'] as const;
export type Theme = (typeof THEMES)[number];

export const REGISTRATION_MODES = ['OPEN', 'INVITE_ONLY', 'CLOSED'] as const;
export type RegistrationMode = (typeof REGISTRATION_MODES)[number];

export const BILLING_PERIODS = ['FREE', 'MONTH', 'YEAR', 'LIFETIME'] as const;
export type BillingPeriod = (typeof BILLING_PERIODS)[number];

/** Тип человека в организации. */
export const PERSON_KINDS = ['PLAYER', 'STAFF', 'CONTRACTOR'] as const;
export type PersonKind = (typeof PERSON_KINDS)[number];

export const PERSON_STATUSES = ['TRIAL', 'ACTIVE', 'INACTIVE', 'SUSPENDED', 'LEFT'] as const;
export type PersonStatus = (typeof PERSON_STATUSES)[number];

export const CONTRACT_TYPES = [
  'SALARY',
  'PER_MATCH',
  'REVSHARE',
  'TRIAL',
  'FIXED_TERM',
  'OTHER',
] as const;
export type ContractType = (typeof CONTRACT_TYPES)[number];

export const CONTRACT_STATUSES = ['DRAFT', 'ACTIVE', 'EXPIRED', 'TERMINATED'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const PAYOUT_PERIODS = ['MONTHLY', 'BIWEEKLY', 'WEEKLY', 'CUSTOM'] as const;
export type PayoutPeriod = (typeof PAYOUT_PERIODS)[number];

export const ADVANCE_TYPES = ['PERCENT', 'FIXED'] as const;
export type AdvanceType = (typeof ADVANCE_TYPES)[number];

/**
 * Кто платит налог.
 * ORG_PAYS     — налог сверху, платит организация. Человек получает всю сумму.
 * PERSON_PAYS  — налог удерживается из суммы. Человек получает меньше.
 * NO_TAX       — налога нет.
 */
export const TAX_MODES = ['ORG_PAYS', 'PERSON_PAYS', 'NO_TAX'] as const;
export type TaxMode = (typeof TAX_MODES)[number];

export const WALLET_TYPES = ['BANK', 'CARD', 'CASH', 'CRYPTO', 'EWALLET', 'OTHER'] as const;
export type WalletType = (typeof WALLET_TYPES)[number];

export const CATEGORY_KINDS = ['INCOME', 'EXPENSE'] as const;
export type CategoryKind = (typeof CATEGORY_KINDS)[number];

export const TRANSACTION_DIRECTIONS = ['IN', 'OUT'] as const;
export type TransactionDirection = (typeof TRANSACTION_DIRECTIONS)[number];

export const PAYOUT_KINDS = [
  'SALARY',
  'ADVANCE',
  'BONUS',
  'PRIZE',
  'REIMBURSEMENT',
  'PENALTY',
  'OTHER',
] as const;
export type PayoutKind = (typeof PAYOUT_KINDS)[number];

/** Виды начислений, которые являются вычетом (уменьшают выплату). */
export const DEDUCTION_KINDS: readonly PayoutKind[] = ['PENALTY'];

/**
 * Ручной статус обработки выплаты (F-35).
 *
 * Существует отдельно от денежного статуса намеренно: денежный выводится из
 * сумм и потому не может врать, а этот отражает то, что происходит вне
 * системы — «перевод отправил, ждёт зачисления», «банк отбил платёж».
 */
export const PAYOUT_PROGRESS_STATUSES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'CONFIRMED',
  'ISSUE',
] as const;
export type PayoutProgressStatus = (typeof PAYOUT_PROGRESS_STATUSES)[number];

export const PAYOUT_STATUSES = [
  'DRAFT',
  'SCHEDULED',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
  'CANCELED',
] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];

export const BILL_STATUSES = ['SCHEDULED', 'PAID', 'OVERDUE', 'SKIPPED', 'CANCELED'] as const;
export type BillStatus = (typeof BILL_STATUSES)[number];

export const INCOME_INVOICE_STATUSES = [
  'SCHEDULED',
  'RECEIVED',
  'OVERDUE',
  'CANCELED',
] as const;
export type IncomeInvoiceStatus = (typeof INCOME_INVOICE_STATUSES)[number];

export const INCOME_SOURCE_TYPES = ['SPONSOR', 'PARTNER', 'MEDIA', 'MERCH', 'OTHER'] as const;
export type IncomeSourceType = (typeof INCOME_SOURCE_TYPES)[number];

/** Правила повторения для регулярных платежей и доходов. */
export const RECURRENCE_RULES = [
  'ONE_TIME',
  'WEEKLY',
  'MONTHLY',
  'EVERY_N_MONTHS',
  'YEARLY',
] as const;
export type RecurrenceRule = (typeof RECURRENCE_RULES)[number];

export const BUDGET_PERIODS = ['MONTH', 'QUARTER', 'YEAR'] as const;
export type BudgetPeriod = (typeof BUDGET_PERIODS)[number];

export const TEMPLATE_KINDS = ['TRANSACTION', 'PAYOUT', 'BILL'] as const;
export type TemplateKind = (typeof TEMPLATE_KINDS)[number];

export const PERSON_DOCUMENT_TYPES = [
  'PASSPORT',
  'VISA',
  'INSURANCE',
  'MEDICAL',
  'TRANSFER_WINDOW',
  'OTHER',
] as const;
export type PersonDocumentType = (typeof PERSON_DOCUMENT_TYPES)[number];

export const DOC_TEMPLATE_KINDS = ['CONTRACT', 'ACT', 'INVOICE', 'OTHER'] as const;
export type DocTemplateKind = (typeof DOC_TEMPLATE_KINDS)[number];

export const CUSTOM_FIELD_TYPES = ['TEXT', 'NUMBER', 'DATE', 'SELECT', 'CHECKBOX'] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const NOTIFICATION_TYPES = [
  'PAYOUT_DUE_SOON',
  'PAYOUT_OVERDUE',
  'BILL_DUE_SOON',
  'BILL_OVERDUE',
  'INCOME_DUE_SOON',
  'INCOME_OVERDUE',
  'CONTRACT_EXPIRING',
  'DOCUMENT_EXPIRING',
  'LOW_BALANCE',
  'BUDGET_EXCEEDED',
  'SUPPORT_REPLY',
  'SUPPORT_NEW_TICKET',
  'DAILY_DIGEST',
  'SYSTEM',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const NOTIFICATION_SEVERITIES = ['INFO', 'WARN', 'DANGER'] as const;
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];

export const SUPPORT_CATEGORIES = ['BILLING', 'BUG', 'QUESTION', 'FEATURE', 'OTHER'] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

export const SUPPORT_PRIORITIES = ['LOW', 'NORMAL', 'HIGH'] as const;
export type SupportPriority = (typeof SUPPORT_PRIORITIES)[number];

export const SUPPORT_STATUSES = [
  'OPEN',
  'IN_PROGRESS',
  'WAITING_USER',
  'RESOLVED',
  'CLOSED',
] as const;
export type SupportStatus = (typeof SUPPORT_STATUSES)[number];

export const SUPPORT_AUTHOR_ROLES = ['USER', 'ADMIN', 'SYSTEM'] as const;
export type SupportAuthorRole = (typeof SUPPORT_AUTHOR_ROLES)[number];

export const TRASH_ENTITY_TYPES = [
  'PERSON',
  'CONTRACT',
  'WALLET',
  'TRANSACTION',
  'PAYOUT',
  'RECURRING_PAYMENT',
  'BILL',
  'INCOME_SOURCE',
  'CATEGORY',
  'TEAM',
] as const;
export type TrashEntityType = (typeof TRASH_ENTITY_TYPES)[number];

export const BACKUP_KINDS = ['AUTO', 'MANUAL'] as const;
export type BackupKind = (typeof BACKUP_KINDS)[number];

/** Порядок приоритета ролей воркспейса: чем больше число, тем больше прав. */
export const WORKSPACE_ROLE_WEIGHT: Record<WorkspaceRole, number> = {
  VIEWER: 1,
  MANAGER: 2,
  ADMIN: 3,
  OWNER: 4,
};

export function hasAtLeastRole(actual: WorkspaceRole, required: WorkspaceRole): boolean {
  return WORKSPACE_ROLE_WEIGHT[actual] >= WORKSPACE_ROLE_WEIGHT[required];
}
